/**
 * src/pages/AssistantPayslips.tsx
 * アシスタント自分専用：支払済み給与明細の一覧・表示・印刷
 *
 * 対象は status='paid' のみ（サーバー側 GET /api/my/payroll で保証）。
 * PDF生成は管理者側と同じ PayslipPreview（buildPayslipHTML / 印刷ダイアログ）をそのまま再利用する。
 */

import { useEffect, useState } from 'react'
import { api } from '../api/client'
import { PayslipPreview, type PayrollRecord } from '../components/PayslipPreview'
import { BonusSlipPreview, type BonusRecord } from '../components/BonusSlipPreview'

type PayslipListItem = {
  id: string
  payroll_month: string
  pay_date: string | null
  net_pay: number
  status: string
}

type BonusListItem = {
  id: string
  bonus_name: string
  payment_date: string
  net_amount: number
  status: string
}

function fmt(n: number | null | undefined) { return (n ?? 0).toLocaleString('ja-JP') }

export default function AssistantPayslips() {
  const [items, setItems]     = useState<PayslipListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)
  const [previewRecord, setPreviewRecord] = useState<PayrollRecord | null>(null)

  const [bonusItems, setBonusItems]     = useState<BonusListItem[]>([])
  const [bonusError, setBonusError]     = useState<string | null>(null)
  const [previewBonus, setPreviewBonus] = useState<BonusRecord | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.my.payslips.list()
      .then((rows) => { if (!cancelled) setItems(rows) })
      .catch((e: any) => { if (!cancelled) setError(e.message ?? '取得に失敗しました') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    api.my.bonuses.list()
      .then((rows) => { if (!cancelled) setBonusItems(rows) })
      .catch(() => { if (!cancelled) setBonusItems([]) })
    return () => { cancelled = true }
  }, [])

  const hasBonuses = bonusItems.length > 0

  const openPreview = async (id: string) => {
    setError(null)
    try {
      const record = await api.my.payslips.get(id)
      setPreviewRecord(record as PayrollRecord)
    } catch (e: any) {
      setError(e.message ?? '給与明細の取得に失敗しました')
    }
  }

  const openBonusPreview = async (id: string) => {
    setBonusError(null)
    try {
      const record = await api.my.bonuses.get(id)
      setPreviewBonus(record as BonusRecord)
    } catch (e: any) {
      setBonusError(e.message ?? '賞与明細の取得に失敗しました')
    }
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <span className="card-title">給与明細</span>
        </div>

        {loading ? (
          <div className="loading" style={{ padding: 48 }}>LOADING...</div>
        ) : error ? (
          <div style={{ padding: 24, color: 'var(--red)' }}>{error}</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--ink-3)', fontSize: 13 }}>
            支払済みの給与明細はまだありません
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>対象月</th>
                  <th>支払額</th>
                  <th>支払日</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.payroll_month}</td>
                    <td>¥{fmt(r.net_pay)}</td>
                    <td>{r.pay_date ?? '—'}</td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={() => openPreview(r.id)}>
                        給与明細を表示
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 賞与明細: 本人に公開可能（paid）な賞与が1件以上ある場合のみ表示。0件・取得失敗時はセクションごと非表示 */}
      {hasBonuses && (
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header">
          <span className="card-title">賞与明細</span>
        </div>

        {bonusError && (
          <div style={{ padding: 24, color: 'var(--red)' }}>{bonusError}</div>
        )}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>賞与名</th>
                <th>支給額</th>
                <th>支給日</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {bonusItems.map((r) => (
                <tr key={r.id}>
                  <td>{r.bonus_name}</td>
                  <td>¥{fmt(r.net_amount)}</td>
                  <td>{r.payment_date ?? '—'}</td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={() => openBonusPreview(r.id)}>
                      賞与明細を表示
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {previewRecord && (
        <PayslipPreview
          record={previewRecord}
          onClose={() => setPreviewRecord(null)}
          onLogIssue={() => { void api.my.payslips.logIssue(previewRecord.id).catch(() => {}) }}
        />
      )}

      {previewBonus && (
        <BonusSlipPreview
          record={previewBonus}
          onClose={() => setPreviewBonus(null)}
          onLogIssue={() => { void api.my.bonuses.logIssue(previewBonus.id).catch(() => {}) }}
        />
      )}
    </div>
  )
}
